import {useCallback, useEffect, useRef, useState} from 'react';
import {followApi} from '../../../../services/followApi';
import {
  FeedAuthor,
  FeedPost,
  PostReactionType,
  socialFeedApi,
} from '../../../../services/socialFeedApi';
import {getAuthorName, isRequestCanceled} from '../utils';

const HOME_POSTS_PAGE_SIZE = 10;
const SHARED_POST_HIGHLIGHT_DURATION_MS = 3200;

const isShareCanceled = (error: unknown) => (
  error instanceof DOMException && error.name === 'AbortError'
) || (
  error instanceof Error && (
    error.name === 'AbortError' ||
    error.message.toLowerCase().includes('cancel')
  )
);

const buildSharedPostUrl = (postId: string) => {
  const shareUrl = new URL(window.location.href);
  shareUrl.pathname = '/';
  shareUrl.search = '';
  shareUrl.hash = '';
  shareUrl.searchParams.set('post', postId);
  return shareUrl.toString();
};

const copyTextWithFallback = async (value: string) => {
  if (window.isSecureContext && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = value;
  textArea.setAttribute('readonly', 'true');
  textArea.style.position = 'fixed';
  textArea.style.top = '-9999px';
  textArea.style.left = '-9999px';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const copied = document.execCommand('copy');

    if (!copied) {
      throw new Error('Copy command was not successful.');
    }
  } finally {
    document.body.removeChild(textArea);
  }
};

const updateFeedAuthorFollowState = (
  author: FeedAuthor | null,
  authorId: string,
  isFollowing: boolean
) => {
  if (!author || author._id !== authorId) {
    return author;
  }

  return {
    ...author,
    isFollowing,
  };
};

const updatePostAuthorFollowState = (
  originalPost: FeedPost['originalPost'],
  authorId: string,
  isFollowing: boolean
): FeedPost['originalPost'] => {
  if (!originalPost) {
    return null;
  }

  const nextOriginalAuthor = updateFeedAuthorFollowState(originalPost.authorId, authorId, isFollowing);

  if (nextOriginalAuthor === originalPost.authorId) {
    return originalPost;
  }

  return {
    ...originalPost,
    authorId: nextOriginalAuthor,
  };
};

const applyAuthorFollowStateToPost = (post: FeedPost, authorId: string, isFollowing: boolean): FeedPost => {
  const nextAuthor = updateFeedAuthorFollowState(post.authorId, authorId, isFollowing);
  const nextOriginalPost = updatePostAuthorFollowState(post.originalPost, authorId, isFollowing);

  if (nextAuthor === post.authorId && nextOriginalPost === post.originalPost) {
    return post;
  }

  return {
    ...post,
    authorId: nextAuthor,
    originalPost: nextOriginalPost,
  };
};

export const useHomePosts = (
  token: string | null,
  sharedPostId: string | null = null,
  onFollowingCountDelta?: (delta: number) => void,
  topic?: string | null
) => {
  const postsRequestControllerRef = useRef<AbortController | null>(null);
  const reactionRequestControllersRef = useRef<Record<string, AbortController>>({});
  const followRequestControllersRef = useRef<Record<string, AbortController>>({});
  const sharedPostRequestControllerRef = useRef<AbortController | null>(null);
  const highlightTimeoutRef = useRef<number | null>(null);
  const activeSharedPostRequestIdRef = useRef<string | null>(null);
  const lastHighlightedSharedPostIdRef = useRef<string | null>(null);
  const lastFailedSharedPostIdRef = useRef<string | null>(null);

  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [postsError, setPostsError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMorePosts, setHasMorePosts] = useState(false);
  const [loadMorePostsLoading, setLoadMorePostsLoading] = useState(false);
  const [loadMorePostsError, setLoadMorePostsError] = useState<string | null>(null);
  const [postActionError, setPostActionError] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});
  const [reactingPostIds, setReactingPostIds] = useState<Record<string, boolean>>({});
  const [pendingFollowAuthorIds, setPendingFollowAuthorIds] = useState<Record<string, boolean>>({});
  const [highlightedPostId, setHighlightedPostId] = useState<string | null>(null);
  const deletingPostIdsRef = useRef(new Set<string>());
  const [deletingPostIds, setDeletingPostIds] = useState<Record<string, boolean>>({});

  const handleDeletePost = useCallback(async (post: FeedPost, currentUserId: string): Promise<boolean> => {
    if (!token || post.authorId?._id !== currentUserId || deletingPostIdsRef.current.has(post._id)) {
      return false;
    }
    if (!window.confirm('Delete this post permanently? Its comments will also be deleted.')) {
      return false;
    }
    deletingPostIdsRef.current.add(post._id);
    setDeletingPostIds(current => ({...current, [post._id]: true}));
    setPostActionError(null);
    try {
      await socialFeedApi.deletePost(token, post._id);
      setPosts(current => current.filter(item => item._id !== post._id).map(item => (
        item.originalPost?._id === post._id ? {...item, originalPost: null} : item
      )));
      lastFailedSharedPostIdRef.current = post._id;
      setHighlightedPostId(current => current === post._id ? null : current);
      return true;
    } catch (error) {
      setPostActionError(error instanceof Error ? error.message : 'Failed to delete post');
      return false;
    } finally {
      deletingPostIdsRef.current.delete(post._id);
      setDeletingPostIds(current => ({...current, [post._id]: false}));
    }
  }, [token]);

  const abortPostsRequest = useCallback(() => {
    postsRequestControllerRef.current?.abort();
    postsRequestControllerRef.current = null;
  }, []);

  const abortSharedPostRequest = useCallback(() => {
    sharedPostRequestControllerRef.current?.abort();
    sharedPostRequestControllerRef.current = null;
    activeSharedPostRequestIdRef.current = null;
  }, []);

  const clearHighlightTimeout = useCallback(() => {
    if (highlightTimeoutRef.current !== null) {
      window.clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = null;
    }
  }, []);

  const highlightPost = useCallback((postId: string) => {
    clearHighlightTimeout();
    setHighlightedPostId(postId);
    lastHighlightedSharedPostIdRef.current = postId;
    highlightTimeoutRef.current = window.setTimeout(() => {
      setHighlightedPostId((currentPostId) => (
        currentPostId === postId ? null : currentPostId
      ));
      highlightTimeoutRef.current = null;
    }, SHARED_POST_HIGHLIGHT_DURATION_MS);
  }, [clearHighlightTimeout]);

  const createPostsRequestSignal = useCallback(() => {
    abortPostsRequest();
    const controller = new AbortController();
    postsRequestControllerRef.current = controller;
    return controller.signal;
  }, [abortPostsRequest]);

  const fetchPosts = useCallback(async () => {
    abortPostsRequest();

    if (!token) {
      setPosts([]);
      setFailedImages({});
      setReactingPostIds({});
      setPendingFollowAuthorIds({});
      setNextCursor(null);
      setHasMorePosts(false);
      setLoadMorePostsLoading(false);
      setLoadMorePostsError(null);
      setPostsError(null);
      setPostActionError(null);
      setPostsLoading(false);
      return;
    }

    const requestSignal = createPostsRequestSignal();

    try {
      setPostsLoading(true);
      setPostsError(null);
      setNextCursor(null);
      setHasMorePosts(false);
      setLoadMorePostsLoading(false);
      setLoadMorePostsError(null);
      setPostActionError(null);

      const response = await socialFeedApi.getPosts(token, {
        limit: HOME_POSTS_PAGE_SIZE,
        topic: topic || undefined,
      }, requestSignal);

      if (requestSignal?.aborted) {
        return;
      }

      setPosts(response.posts);
      setNextCursor(response.nextCursor ?? null);
      setHasMorePosts(Boolean(response.nextCursor));
      setFailedImages({});
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      setPostsError(error instanceof Error ? error.message : 'Failed to load posts');
    } finally {
      if (!requestSignal?.aborted) {
        setPostsLoading(false);
      }

      if (postsRequestControllerRef.current?.signal === requestSignal) {
        postsRequestControllerRef.current = null;
      }
    }
  }, [abortPostsRequest, createPostsRequestSignal, token, topic]);

  const loadMorePosts = useCallback(async () => {
    if (!token || !nextCursor || postsLoading || loadMorePostsLoading) {
      return;
    }

    const requestSignal = createPostsRequestSignal();

    try {
      setLoadMorePostsLoading(true);
      setLoadMorePostsError(null);

      const response = await socialFeedApi.getPosts(token, {
        cursor: nextCursor,
        limit: HOME_POSTS_PAGE_SIZE,
        topic: topic || undefined,
      }, requestSignal);

      if (requestSignal.aborted) {
        return;
      }

      setPosts((currentPosts) => [...currentPosts, ...response.posts]);
      setNextCursor(response.nextCursor ?? null);
      setHasMorePosts(Boolean(response.nextCursor));
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      setLoadMorePostsError(error instanceof Error ? error.message : 'Failed to load more posts');
    } finally {
      if (!requestSignal.aborted) {
        setLoadMorePostsLoading(false);
      }

      if (postsRequestControllerRef.current?.signal === requestSignal) {
        postsRequestControllerRef.current = null;
      }
    }
  }, [createPostsRequestSignal, loadMorePostsLoading, nextCursor, postsLoading, token, topic]);

  const handleImageError = useCallback((postId: string, mediaIndex: number) => {
    setFailedImages((currentFailedImages) => ({
      ...currentFailedImages,
      [`${postId}:${mediaIndex}`]: true,
    }));
  }, []);

  const handleSelectReaction = useCallback(async (postId: string, reactionType: PostReactionType | null) => {
    if (!token) {
      setPostActionError('You must be signed in to react to posts.');
      return;
    }

    if (reactingPostIds[postId]) {
      return;
    }

    const controller = new AbortController();
    reactionRequestControllersRef.current[postId] = controller;

    try {
      setPostActionError(null);
      setReactingPostIds((currentReactingPostIds) => ({
        ...currentReactingPostIds,
        [postId]: true,
      }));

      const response = await socialFeedApi.setPostReaction(token, postId, reactionType, controller.signal);

      if (controller.signal.aborted) {
        return;
      }

      setPosts((currentPosts) => currentPosts.map((post) => (
        post._id === postId ? response.post : post
      )));
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      setPostActionError(error instanceof Error ? error.message : 'Failed to update reaction');
    } finally {
      delete reactionRequestControllersRef.current[postId];

      if (!controller.signal.aborted) {
        setReactingPostIds((currentReactingPostIds) => {
          const nextReactingPostIds = {...currentReactingPostIds};
          delete nextReactingPostIds[postId];
          return nextReactingPostIds;
        });
      }
    }
  }, [reactingPostIds, token]);

  const handleToggleAuthorFollow = useCallback(async (authorId: string, isFollowing: boolean) => {
    const normalizedAuthorId = authorId.trim();

    if (!token) {
      setPostActionError('You must be signed in to follow users.');
      return;
    }

    if (!normalizedAuthorId || pendingFollowAuthorIds[normalizedAuthorId]) {
      return;
    }

    const controller = new AbortController();
    followRequestControllersRef.current[normalizedAuthorId] = controller;

    try {
      setPostActionError(null);
      setPendingFollowAuthorIds((currentPendingFollowAuthorIds) => ({
        ...currentPendingFollowAuthorIds,
        [normalizedAuthorId]: true,
      }));

      const response = isFollowing
        ? await followApi.unfollowUser(token, normalizedAuthorId, controller.signal)
        : await followApi.followUser(token, normalizedAuthorId, controller.signal);

      if (controller.signal.aborted) {
        return;
      }

      setPosts((currentPosts) => currentPosts.map((post) => (
        applyAuthorFollowStateToPost(post, response.userId, response.isFollowing)
      )));

      if (response.didChange !== false) {
        onFollowingCountDelta?.(response.isFollowing ? 1 : -1);
      }
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      setPostActionError(error instanceof Error ? error.message : 'Failed to update follow status');
    } finally {
      delete followRequestControllersRef.current[normalizedAuthorId];

      if (!controller.signal.aborted) {
        setPendingFollowAuthorIds((currentPendingFollowAuthorIds) => {
          const nextPendingFollowAuthorIds = {...currentPendingFollowAuthorIds};
          delete nextPendingFollowAuthorIds[normalizedAuthorId];
          return nextPendingFollowAuthorIds;
        });
      }
    }
  }, [onFollowingCountDelta, pendingFollowAuthorIds, token]);

  const handleSharePost = useCallback(async (post: FeedPost): Promise<'shared' | 'copied' | null> => {
    const shareUrl = buildSharedPostUrl(post._id);
    const authorName = getAuthorName(post);
    const sharePayload = {
      title: `${authorName} on XPSR`,
      text: `Check out ${authorName}'s post on XPSR.`,
      url: shareUrl,
    };

    try {
      setPostActionError(null);

      if (window.isSecureContext && typeof navigator.share === 'function') {
        try {
          await navigator.share(sharePayload);
          return 'shared';
        } catch (error) {
          if (isShareCanceled(error)) {
            return null;
          }
        }
      }

      await copyTextWithFallback(shareUrl);
      return 'copied';
    } catch (error) {
      if (isShareCanceled(error)) {
        return null;
      }

      setPostActionError('We could not share this post right now. Please try again.');
      return null;
    }
  }, []);

  const updatePostCommentCount = useCallback((postId: string, commentCount: number) => {
    setPosts((currentPosts) => currentPosts.map((post) => (
      post._id === postId
        ? {
            ...post,
            commentCount: Math.max(0, commentCount),
          }
        : post
    )));
  }, []);

  const prependPost = useCallback((post: FeedPost) => {
    setPosts((currentPosts) => [
      post,
      ...currentPosts.filter((currentPost) => currentPost._id !== post._id),
    ]);
    setPostActionError(null);
    highlightPost(post._id);
  }, [highlightPost]);

  useEffect(() => {
    if (!sharedPostId || topic) {
      abortSharedPostRequest();
      clearHighlightTimeout();
      setHighlightedPostId(null);
      lastHighlightedSharedPostIdRef.current = null;
      lastFailedSharedPostIdRef.current = null;
      return;
    }

    if (!token) {
      return;
    }

    if (posts.some((post) => post._id === sharedPostId)) {
      lastFailedSharedPostIdRef.current = null;

      if (lastHighlightedSharedPostIdRef.current !== sharedPostId) {
        highlightPost(sharedPostId);
      }

      return;
    }

    if (postsLoading || activeSharedPostRequestIdRef.current === sharedPostId || lastFailedSharedPostIdRef.current === sharedPostId) {
      return;
    }

    const controller = new AbortController();
    sharedPostRequestControllerRef.current = controller;
    activeSharedPostRequestIdRef.current = sharedPostId;
    setPostActionError(null);

    void (async () => {
      try {
        const response = await socialFeedApi.getPost(token, sharedPostId, controller.signal);

        if (controller.signal.aborted) {
          return;
        }

        lastFailedSharedPostIdRef.current = null;
        setPosts((currentPosts) => (
          currentPosts.some((post) => post._id === sharedPostId)
            ? currentPosts
            : [response.post, ...currentPosts]
        ));
        highlightPost(sharedPostId);
      } catch (error) {
        if (isRequestCanceled(error)) {
          return;
        }

        lastFailedSharedPostIdRef.current = sharedPostId;
        setPostActionError('We could not open that shared post.');
      } finally {
        if (sharedPostRequestControllerRef.current === controller) {
          sharedPostRequestControllerRef.current = null;
        }

        if (activeSharedPostRequestIdRef.current === sharedPostId) {
          activeSharedPostRequestIdRef.current = null;
        }
      }
    })();

    return () => {
      controller.abort();

      if (sharedPostRequestControllerRef.current === controller) {
        sharedPostRequestControllerRef.current = null;
      }

      if (activeSharedPostRequestIdRef.current === sharedPostId) {
        activeSharedPostRequestIdRef.current = null;
      }
    };
  }, [abortSharedPostRequest, clearHighlightTimeout, highlightPost, posts, postsLoading, sharedPostId, token, topic]);

  useEffect(() => {
    void fetchPosts();

    return () => {
      abortPostsRequest();
      abortSharedPostRequest();
      clearHighlightTimeout();
      Object.values(followRequestControllersRef.current).forEach((followController) => followController.abort());
      followRequestControllersRef.current = {};
      Object.values(reactionRequestControllersRef.current).forEach((reactionController) => reactionController.abort());
      reactionRequestControllersRef.current = {};
    };
  }, [abortPostsRequest, abortSharedPostRequest, clearHighlightTimeout, fetchPosts]);

  return {
    deletingPostIds,
    handleDeletePost,
    posts,
    postsLoading,
    postsError,
    hasMorePosts,
    loadMorePostsLoading,
    loadMorePostsError,
    postActionError,
    failedImages,
    reactingPostIds,
    pendingFollowAuthorIds,
    fetchPosts,
    loadMorePosts,
    handleImageError,
    handleSelectReaction,
    handleToggleAuthorFollow,
    handleSharePost,
    highlightedPostId,
    prependPost,
    updatePostCommentCount,
  };
};
